import React, { useState, useEffect, useMemo } from "react";
import { cn } from "@src/lib/utils/cn";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { useChatContext } from "./ChatRuntimeProvider";
import { ChatSidebar } from "@src/features/aiAssist/uiComponents/ChatSidebar";
import { AiChatType, Ff } from "@src/types.generated";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import Text from "@src/lib/ui/Text";
import { LucideBookText } from "lucide-react";

export const KnowledgeBaseChatSidebar: React.FC = () => {
  const { messages, submitMessage, status, stop } = useChatContext();
  const {
    startNewConversation,
    close,
    isOpen,
    initialQuery,
    clearInitialQuery,
  } = useChatSidebar(AiChatType.KnowledgeBase);
  const askOpusKillSwitchEnabledFeatureFlag = useFeatureFlag(
    Ff.AskOpusKillSwitch,
  );
  const askOpusSuggestedQuestionsEnabledFeatureFlag = useFeatureFlag(
    Ff.AskOpusSuggestedQuestions,
  );
  const [input, setInput] = useState("");

  // Automatically submit the initial query from JumpToBar as a user message when opening the sidebar
  // This enables the "Ask Opus" feature where users can search and immediately start a chat with their query
  useEffect(() => {
    if (initialQuery && initialQuery.trim()) {
      submitMessage({ role: "user", content: initialQuery.trim() });
      clearInitialQuery();
    }
  }, [initialQuery, submitMessage, clearInitialQuery]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleSubmit = () => {
    submitMessage({ role: "user", content: input });
    setInput("");
  };

  const handleQuestionSelect = (questionId: string, questionText: string) => {
    submitMessage({
      role: "user",
      content: questionText,
      customMessageData: {
        suggestedQuestionId: questionId,
      },
    });
  };

  const disabledMessage = useMemo(() => {
    if (askOpusKillSwitchEnabledFeatureFlag) {
      return {
        title: "Ask Opus is disabled",
        description:
          "We're making improvements. Ask Opus will be back online shortly.",
      };
    }

    return undefined;
  }, [askOpusKillSwitchEnabledFeatureFlag]);

  return (
    <div
      className={cn(
        "shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out",
        isOpen ? "w-[326px]" : "w-0",
      )}
      aria-hidden={!isOpen}
    >
      <div className="flex h-full w-[326px] min-w-0 py-2 pr-2">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl rounded-l-none bg-white shadow-out-sm">
          <ChatSidebar
            disabledMessage={disabledMessage}
            headerProps={{
              title: "Ask Opus",
              onClickNewChat: startNewConversation,
              onClickClose: close,
            }}
            threadProps={{
              messages,
              emptyStateTitle: "Hi! I'm Ask Opus.",
              emptyStateTitle2: "How can I assist you today?",
              suggestedQuestionsProps: {
                enabled:
                  messages.length === 0 &&
                  askOpusSuggestedQuestionsEnabledFeatureFlag,
                onQuestionSelect: handleQuestionSelect,
              },
              emptyStateFooter: (
                <div className="mt-6 flex min-w-0 items-start gap-1 px-2">
                  <LucideBookText className="mt-0.5 size-[14px] shrink-0 text-muted-foreground" />
                  <Text
                    variant="fine-print-title"
                    className="min-w-0 text-muted-foreground"
                    multiline
                  >
                    Answers are based on the content in your Opus library
                  </Text>
                </div>
              ),
            }}
            composerProps={{
              input,
              onInputChange: handleInputChange,
              onSubmit: handleSubmit,
              isStreaming: status === "streaming",
              onStop: stop,
            }}
          />
        </div>
      </div>
    </div>
  );
};
